import React, { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { Alert } from "react-bootstrap";
import "./OrganizationsPage.css";
import { PLAN_LIMITS, PlanId, PlanLimits } from "../../api/organizations";
import { GlobalPlanCatalogModal } from "./GlobalPlanCatalogModal";
import {
  IntelligenceCatalogModal,
  IntelligenceShipGateModal,
} from "./intelligence";
import { OrganizationsTab } from "./OrganizationsTab";
import { fetchPlanCatalog } from "../../api/planCatalog";
import { PLAN_IDS } from "./constants";
import { useOrganizationsData } from "./hooks/useOrganizationsData";

const PageIntro = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <header className="content__header mb-4">
    <div>
      <h1 className="page-header__title">{title}</h1>
      <p className="page-header__lead">{description}</p>
    </div>
  </header>
);

const OrganizationsPage: React.FC<{ user: User }> = ({ user }) => {
  const [planCatalog, setPlanCatalog] =
    useState<Record<PlanId, PlanLimits>>(PLAN_LIMITS);
  const [showGlobalPlanModal, setShowGlobalPlanModal] = useState(false);
  const [showIntelligenceCatalogModal, setShowIntelligenceCatalogModal] =
    useState(false);
  const [showIntelligenceShipGateModal, setShowIntelligenceShipGateModal] =
    useState(false);

  const {
    records,
    setRecords,
    loading,
    error,
    setError,
    currentPage,
    hasMore,
    goToPage,
  } = useOrganizationsData();

  useEffect(() => {
    fetchPlanCatalog(user)
      .then((items) => {
        const next = { ...PLAN_LIMITS } as Record<PlanId, PlanLimits>;
        for (const item of items) {
          if (PLAN_IDS.includes(item.id)) {
            next[item.id] = item;
          }
        }
        setPlanCatalog(next);
      })
      .catch(() => {});
  }, [user]);

  return (
    <div className="organizations-page">
      <PageIntro
        title="Organizations"
        description="View and manage organizations: plans, Intelligence trials, and activity."
      />
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="org-page-panel">
        <OrganizationsTab
          records={records}
          setRecords={setRecords}
          loading={loading}
          error={error}
          setError={setError}
          currentPage={currentPage}
          hasMore={hasMore}
          goToPage={goToPage}
          planCatalog={planCatalog}
          onOpenGlobalPlanModal={() => setShowGlobalPlanModal(true)}
          onOpenIntelligenceCatalogModal={() =>
            setShowIntelligenceCatalogModal(true)
          }
          onOpenIntelligenceShipGateModal={() =>
            setShowIntelligenceShipGateModal(true)
          }
          user={user}
        />
      </div>

      <GlobalPlanCatalogModal
        show={showGlobalPlanModal}
        user={user}
        onHide={() => setShowGlobalPlanModal(false)}
        onCatalogUpdated={setPlanCatalog}
      />

      <IntelligenceCatalogModal
        show={showIntelligenceCatalogModal}
        user={user}
        onHide={() => setShowIntelligenceCatalogModal(false)}
      />

      <IntelligenceShipGateModal
        show={showIntelligenceShipGateModal}
        user={user}
        onHide={() => setShowIntelligenceShipGateModal(false)}
      />
    </div>
  );
};

export default OrganizationsPage;
